import { normalizeTeamRoleId, type Team, type TeamMap } from '@wehunt/ui'
import type { GroupDto } from './useMyGroups'

export const EMPTY_TEAM_MAP: TeamMap = { name: '', updated: '', boundaryAreas: [], objects: [] }

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatNextHunt(isoString: string): string {
  const d = new Date(isoString)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}, ${hh}:${mm}`
}

const DEFAULT_TEAM_PHOTOS = [
  '/assets/team-photo-1.jpg',
  '/assets/team-photo-2.jpg',
  '/assets/team-photo-3.jpg',
]

function defaultTeamPhoto(groupId: string): string {
  const idx = groupId.charCodeAt(groupId.length - 1) % DEFAULT_TEAM_PHOTOS.length
  return DEFAULT_TEAM_PHOTOS[idx]
}

/**
 * Map the backend group role string onto the role identifier the UI gates and
 * labels off. Delegates to `@wehunt/ui`'s normalizer so the badge, the team
 * overview and the plan-hunt member lists all place a given wire value in the
 * same bucket — they used to each interpret it separately.
 *
 * The returned value is an id, never display text: the label is looked up per
 * locale at render time. This function previously returned the English words
 * ("Owner", "Administrator", "Member") directly, which is why the badge read
 * English no matter which language was selected.
 */
export function mapGroupRole(role: string): Team['role'] {
  return normalizeTeamRoleId(role)
}

/**
 * Whether the role can edit the team map — add/move pins etc. (CLUB-175,
 * CLUB-311). The backend group roles are owner/admin/member/viewOnly (the
 * mobile app's canonical set); a plain **member** is an editing role and may
 * add pins, only **viewOnly** is read-only. ('edit'/'view' are also accepted
 * for any legacy/alternate wire spelling.) Anything unknown defaults to
 * read-only. Note: border/document editing is further restricted to
 * owner/admin — that gate lives in the editor, not here.
 */
export function canEditRole(role: string): boolean {
  const r = role.trim().toLowerCase()
  return r === 'owner' || r === 'admin' || r === 'edit' || r === 'member'
}

export function groupToTeam(g: GroupDto): Team {
  return {
    id: g.id,
    name: g.name,
    role: mapGroupRole(g.role),
    canEdit: canEditRole(g.role),
    members: g.memberCount,
    isEstate: g.isEstate ?? false,
    area: g.region ?? '',
    hectares: Math.round(g.hectares),
    // Empty when there is no upcoming hunt; the UI renders the empty-state
    // label (NO_PLANNED_HUNTS_LABEL) so the copy lives in one place.
    nextHunt: g.nextHuntAt ? formatNextHunt(g.nextHuntAt) : '',
    photo: g.photoUrl ?? defaultTeamPhoto(g.id),
    joinCode: g.joinCode ?? '',
    // Spread rather than assign: EMPTY_TEAM_MAP is one shared object, so setting a
    // field on it would give every team the last team's colour.
    //
    // `map.borderColor` is the field the canvas has read since before this change and
    // that nothing in production had ever filled — the only assignments were two
    // fixtures. Filling it here puts the team's colour on every surface at once,
    // because each already resolves a border as its own colour, then this, then the
    // default.
    map: { ...EMPTY_TEAM_MAP, borderColor: g.borderColor },
  }
}
